/**
 * Layout 2: Topographic
 * Cartographic/Technical aesthetic inspired by survey maps
 * - Contour line patterns and grid systems
 * - Technical typography with coordinate labels
 * - Monospace accents, precise geometric layouts
 */
import { useCallback, useMemo, useRef, useState } from "react";
import { NavigationControl, ScaleControl } from "react-map-gl/mapbox";
import type { Map as MapboxMap } from "mapbox-gl";
import {
  type PublishedLandingPage,
  type PlaceFeature,
  type PlaceGeoJSON,
  PLACE_TYPE_META,
  PlaceType,
  getPlaceTrack,
} from "../../lib/api";
import { FIT_BOUNDS_PADDING, FLY_TO_OPTIONS, FLY_TO_ZOOM } from "../../lib/mapConstants";
import { routes } from "../../lib/routes";
import { useShare } from "../../hooks/useShare";
import { NomaMap, AdventureMapPlacesLayer, SelectedPlaceLayer, RouteTrackLayer } from "../../components/map";

interface TopographicLandingProps {
  mapData: PublishedLandingPage;
  places: PlaceGeoJSON;
}

export default function TopographicLanding({ mapData, places }: TopographicLandingProps) {
  const share = useShare();
  const mapSectionRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<PlaceFeature | null>(null);

  const placesByType = useMemo(() => {
    const grouped = new Map<PlaceType, PlaceFeature[]>();
    places.features.forEach((place) => {
      const type = place.properties.type;
      if (!grouped.has(type)) grouped.set(type, []);
      grouped.get(type)!.push(place);
    });
    return grouped;
  }, [places.features]);

  const handleMapLoad = useCallback((map: MapboxMap) => {
    mapRef.current = map;
  }, []);

  const handlePlaceSelect = useCallback(
    (placeId: string) => {
      const place = places.features.find((f) => f.id === placeId);
      if (!place) return;
      setSelectedPlace(place);
      mapRef.current?.flyTo({
        ...FLY_TO_OPTIONS,
        center: place.geometry.coordinates as [number, number],
        zoom: FLY_TO_ZOOM,
      });
      if (mapSectionRef.current) {
        const headerHeight = 60;
        const top = mapSectionRef.current.getBoundingClientRect().top + window.scrollY - headerHeight;
        window.scrollTo({ top, behavior: "smooth" });
      }
    },
    [places.features],
  );

  const handlePlaceClick = useCallback(
    (feature: PlaceFeature) => {
      handlePlaceSelect(feature.id as string);
    },
    [handlePlaceSelect],
  );

  const handleShare = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (mapData.slug) {
        share({
          url: routes.to.landingPages.bySlug(mapData.slug),
          title: mapData.name,
        });
      }
    },
    [share, mapData.slug, mapData.name],
  );

  const formatCoord = (coord: number, isLat: boolean) => {
    const dir = isLat ? (coord >= 0 ? "N" : "S") : coord >= 0 ? "E" : "W";
    return `${Math.abs(coord).toFixed(4)}${dir}`;
  };

  const typeSymbols: Partial<Record<PlaceType, string>> = {
    restaurant: "REST",
    cafe: "CAFE",
    "grocery-store": "GROC",
    hiking: "HIKE",
    accommodation: "STAY",
  };
  const getTypeSymbol = (type: PlaceType) => typeSymbols[type] ?? "SPOT";

  // SVG pattern for contour lines
  const contourPattern = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 30 Q15 20 30 30 T60 30' fill='none' stroke='%23cbd5c0' stroke-width='0.5'/%3E%3Cpath d='M0 45 Q15 35 30 45 T60 45' fill='none' stroke='%23cbd5c0' stroke-width='0.5'/%3E%3Cpath d='M0 15 Q15 5 30 15 T60 15' fill='none' stroke='%23cbd5c0' stroke-width='0.5'/%3E%3C/svg%3E")`;

  return (
    <div
      className="min-h-screen"
      style={{
        fontFamily: "'IBM Plex Mono', 'Consolas', monospace",
        background: "#f5f7f2",
      }}
    >
      {/* Technical header bar */}
      <header
        className="sticky top-0 z-50 px-6 py-4 flex items-center justify-between border-b"
        style={{ background: "#f5f7f2", borderColor: "#c5cdb8" }}
      >
        <div className="flex items-center gap-6">
          <div className="text-sm tracking-wider" style={{ color: "#6b7a5d" }}>
            TOPO/MAP/{new Date().getFullYear()}
          </div>
          <div className="h-4 w-px" style={{ background: "#c5cdb8" }} />
          <h1 className="text-base font-medium tracking-wide" style={{ color: "#3a4a2e" }}>
            {mapData.name.toUpperCase()}
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm" style={{ color: "#8b9a7a" }}>
            REF: {mapData.slug?.toUpperCase() || "---"}
          </span>
          <button
            onClick={handleShare}
            className="px-5 py-2 text-sm tracking-wider transition-colors hover:opacity-80 flex items-center gap-2"
            style={{ background: "#3a4a2e", color: "#f5f7f2" }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 12V14H12V12M8 2V10M8 2L5 5M8 2L11 5" />
            </svg>
            SHARE
          </button>
        </div>
      </header>

      {/* Map Section with grid overlay */}
      <div ref={mapSectionRef} className="relative p-8" style={{ backgroundImage: contourPattern }}>
        {/* Grid lines overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `
            linear-gradient(to right, #c5cdb8 1px, transparent 1px),
            linear-gradient(to bottom, #c5cdb8 1px, transparent 1px)
          `,
            backgroundSize: "80px 80px",
            opacity: 0.4,
          }}
        />

        {/* Map container */}
        <div className="relative h-[60vh] w-full border" style={{ borderColor: "#3a4a2e" }}>
          <NomaMap bounds={places} padding={FIT_BOUNDS_PADDING.desktop} onLoad={handleMapLoad}>
            <NavigationControl position="bottom-right" />
            <ScaleControl position="bottom-left" maxWidth={100} unit="metric" />
            <AdventureMapPlacesLayer places={places} onClick={handlePlaceClick} />
            <RouteTrackLayer track={selectedPlace ? getPlaceTrack(selectedPlace.properties) : undefined} />
            <SelectedPlaceLayer place={selectedPlace} />
          </NomaMap>
        </div>

        {/* Corner coordinates */}
        <div className="absolute top-2 left-2 text-xs tracking-wider" style={{ color: "#6b7a5d" }}>
          NW
        </div>
        <div className="absolute top-2 right-2 text-xs tracking-wider" style={{ color: "#6b7a5d" }}>
          NE
        </div>
        <div className="absolute bottom-2 left-2 text-xs tracking-wider" style={{ color: "#6b7a5d" }}>
          SW
        </div>
        <div className="absolute bottom-2 right-2 text-xs tracking-wider" style={{ color: "#6b7a5d" }}>
          SE
        </div>

        {/* Title overlay */}
        <div
          className="absolute top-12 left-12 max-w-md p-5"
          style={{
            background: "rgba(245,247,242,0.95)",
            border: "1px solid #3a4a2e",
          }}
        >
          <div className="text-xs tracking-[0.3em] mb-2" style={{ color: "#6b7a5d" }}>
            SURVEY AREA
          </div>
          <h2 className="text-2xl font-medium tracking-wide" style={{ color: "#3a4a2e" }}>
            {mapData.name}
          </h2>
          {mapData.description && (
            <p className="text-sm mt-3 leading-relaxed" style={{ color: "#5a6a4a" }}>
              {mapData.description}
            </p>
          )}
          <div className="text-xs mt-4 pt-3 border-t" style={{ borderColor: "#c5cdb8", color: "#8b9a7a" }}>
            POINTS: {places.features.length} | CATEGORIES: {placesByType.size}
          </div>
        </div>

        {/* Selected place detail */}
        {selectedPlace && (
          <div className="absolute top-12 right-12 w-80 animate-in fade-in slide-in-from-right-2 duration-200">
            <div
              className="p-5"
              style={{
                background: "rgba(245,247,242,0.98)",
                border: "1px solid #3a4a2e",
              }}
            >
              <div className="flex justify-between items-start mb-3">
                <div
                  className="px-2 py-1 text-xs tracking-wider"
                  style={{
                    background: "#3a4a2e",
                    color: "#f5f7f2",
                  }}
                >
                  {getTypeSymbol(selectedPlace.properties.type)}
                </div>
                <button
                  onClick={() => setSelectedPlace(null)}
                  className="text-sm hover:opacity-60"
                  style={{ color: "#3a4a2e" }}
                >
                  [X]
                </button>
              </div>
              <h3 className="text-lg font-medium mb-2" style={{ color: "#3a4a2e" }}>
                {selectedPlace.properties.name}
              </h3>
              <div className="text-xs mb-3" style={{ color: "#6b7a5d" }}>
                LAT: {formatCoord(selectedPlace.geometry.coordinates[1], true)} | LON:{" "}
                {formatCoord(selectedPlace.geometry.coordinates[0], false)}
              </div>
              {selectedPlace.properties.description && (
                <div
                  className="text-sm leading-relaxed pt-3 border-t"
                  style={{
                    borderColor: "#c5cdb8",
                    color: "#5a6a4a",
                  }}
                >
                  NOTE: {selectedPlace.properties.description}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Legend / Index Section */}
      <main className="max-w-6xl mx-auto px-8 py-16">
        {/* Section header */}
        <div className="flex items-center gap-4 mb-12 pb-4 border-b" style={{ borderColor: "#c5cdb8" }}>
          <span className="text-xs tracking-[0.3em]" style={{ color: "#6b7a5d" }}>
            INDEX
          </span>
          <div className="flex-1 h-px" style={{ background: "#c5cdb8" }} />
          <span className="text-xs" style={{ color: "#8b9a7a" }}>
            {places.features.length} ENTRIES
          </span>
        </div>

        {places.features.length === 0 ? (
          <div className="text-center py-16 text-base" style={{ color: "#8b9a7a" }}>
            NO DATA POINTS RECORDED
          </div>
        ) : (
          <div className="space-y-12">
            {Array.from(placesByType.entries()).map(([type, typePlaces]) => (
              <section key={type}>
                {/* Category header */}
                <div className="flex items-center gap-4 mb-6">
                  <div
                    className="px-3 py-1 text-xs tracking-wider"
                    style={{
                      background: "#3a4a2e",
                      color: "#f5f7f2",
                    }}
                  >
                    {getTypeSymbol(type)}
                  </div>
                  <span className="text-base tracking-wide" style={{ color: "#3a4a2e" }}>
                    {PLACE_TYPE_META[type].labelPlural.toUpperCase()}
                  </span>
                  <span className="text-sm" style={{ color: "#8b9a7a" }}>
                    ({typePlaces.length})
                  </span>
                </div>

                {/* Places table */}
                <div className="border" style={{ borderColor: "#c5cdb8" }}>
                  {/* Table header */}
                  <div
                    className="grid grid-cols-12 gap-4 px-5 py-3 text-xs tracking-wider border-b"
                    style={{
                      background: "#eef2e8",
                      borderColor: "#c5cdb8",
                      color: "#6b7a5d",
                    }}
                  >
                    <div className="col-span-1">ID</div>
                    <div className="col-span-4">NAME</div>
                    <div className="col-span-2">LAT</div>
                    <div className="col-span-2">LON</div>
                    <div className="col-span-3">NOTES</div>
                  </div>

                  {/* Table rows */}
                  {typePlaces.map((place, idx) => (
                    <button
                      key={place.id}
                      onClick={() => handlePlaceSelect(place.id as string)}
                      className="w-full grid grid-cols-12 gap-4 px-5 py-4 text-left text-sm border-b last:border-b-0 transition-colors hover:bg-[#eef2e8]"
                      style={{
                        borderColor: "#c5cdb8",
                        background: selectedPlace?.id === place.id ? "#e5eadc" : "transparent",
                      }}
                    >
                      <div className="col-span-1" style={{ color: "#8b9a7a" }}>
                        {String(idx + 1).padStart(2, "0")}
                      </div>
                      <div className="col-span-4 font-medium" style={{ color: "#3a4a2e" }}>
                        {place.properties.name}
                      </div>
                      <div className="col-span-2" style={{ color: "#6b7a5d" }}>
                        {formatCoord(place.geometry.coordinates[1], true)}
                      </div>
                      <div className="col-span-2" style={{ color: "#6b7a5d" }}>
                        {formatCoord(place.geometry.coordinates[0], false)}
                      </div>
                      <div className="col-span-3 truncate" style={{ color: "#5a6a4a" }}>
                        {place.properties.description || "---"}
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t py-12 px-8" style={{ borderColor: "#c5cdb8" }}>
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-8">
            <p className="text-base" style={{ color: "#5a6a4a" }}>
              Create your own adventure map
            </p>
            <a
              href={routes.to.home()}
              className="px-6 py-3 text-sm tracking-wider transition-colors hover:opacity-80"
              style={{ background: "#3a4a2e", color: "#f5f7f2" }}
            >
              GET STARTED WITH NOMA.TO
            </a>
          </div>
          <div
            className="flex justify-between items-center text-xs tracking-wider pt-6 border-t"
            style={{ color: "#8b9a7a", borderColor: "#c5cdb8" }}
          >
            <div>
              GENERATED BY {(mapData.curatedBy?.displayName ?? "YOUR LOCAL ADVENTURERS").toUpperCase()} |{" "}
              {new Date().toISOString().split("T")[0]}
            </div>
            <div>VER 1.0 | TOPO SERIES</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
